import {
  AUDITLOG_COLUMN,
  AUDITLOG_SRC,
  AuditLogRecord
} from 'core/auditLog/AuditLog';
import { AbstractAuditLogModel } from './AuditLogModel';
import {
  AuditLogManager,
  DefaultAuditLogManager
} from 'core/auditLog/AuditLogManager';
import { toast } from 'react-toastify';
import _ from 'lodash';
import formatters from './AuditLogFormatters';
import {
  ColumnDescription,
  ExpandRowProps
} from 'components/common/Table/types';
import { renderColumn } from 'components/TableColumn/TableColumn';

export type ChannelGroupAuditLogListRow = {
  id: number;
  [AUDITLOG_COLUMN.DETAIL]: {
    [key: string]: {
      adminOnly?: boolean;
      org: string;
      to: string;
    };
  }[];
  [AUDITLOG_COLUMN.DATE]: string;
  [AUDITLOG_COLUMN.SOURCE_INFO]: {
    id: number;
    type: AUDITLOG_SRC;
    name: string;
  };
  [AUDITLOG_COLUMN.EMAIL]: string;
  [AUDITLOG_COLUMN.EVENT]: string;
};

/**
 * ECOPD-5701：投遞渠道詳情頁的「操作歷程紀錄」。形狀照抄 `AgencyAuditLogModel`，
 * 三處刻意的差別：
 *
 * 1. 不做欄位下拉篩選 —— `getFilters()` 回 `{}`（**不要**呼叫 `super.getFilters()`，
 *    父類別一定會塞 `fieldFilter`），連帶不需要後端的 msgKeys 端點。
 * 2. 排序走真正的 `ctime`（manager 對這條線不套 `replaceColumnNameToSortField`）。
 * 3. 沒有 isAdmin 參數 —— 這個畫面只有 sysadmin 進得來。
 *
 * 回傳該渠道的**全部**變更，不只停用事件。
 */
export class ChannelGroupAuditLogModel extends AbstractAuditLogModel<ChannelGroupAuditLogListRow> {
  nonExpandRowKey: number[] = [];

  constructor(
    public channelGroupId: number,
    auditLogManager: AuditLogManager = new DefaultAuditLogManager()
  ) {
    super(auditLogManager);
  }

  getIgnoreFields() {
    return [];
  }

  /**
   * ECOPD-5701：只有日期欄可排序。這條線不套 `replaceColumnNameToSortField`，
   * 點其他表頭會把未經翻譯的 dataField 直接當 sort 送出（例如 `sort=email`），
   * 而後端只保證支援 `ctime`。spec 沒要求其他欄排序，直接關掉。
   */
  override getAuditLogColumnDefinition(columnName) {
    return {
      ...super.getAuditLogColumnDefinition(columnName),
      sort: columnName === AUDITLOG_COLUMN.DATE
    };
  }

  override getFilters(): {
    [key: string]: {
      options: SelectOptions[];
      value?: string | number;
      onSelect: (target: string | number) => void;
    };
  } {
    return {};
  }

  getSubFilters(): {
    [key: string]: {
      options: SelectOptions[];
      value?: string | number;
      onSelect: (target: string | number) => void;
    };
  } {
    return {};
  }

  /** 不做欄位篩選，所以不打 msgKeys。`init(page, true)` 呼叫到這裡就是個 no-op。 */
  async getAuditLogFields() {
    this.auditLogFields = [];
  }

  getAuditLogDataOfRecord = (record: AuditLogRecord) => {
    // msg 為空的沒有明細可展開，記起來交給 `getExpandRenderer`。
    if (!record.msg || record.msg.length === 0) {
      this.nonExpandRowKey.push(record.id);
    }
    return {
      id: record.id,
      // msg 可能是 null，正規化成空陣列（這些列已進 `nonExpandRowKey`，展不開）。
      [AUDITLOG_COLUMN.DETAIL]: record.msg ?? [],
      [AUDITLOG_COLUMN.DATE]: record.ctime,
      [AUDITLOG_COLUMN.SOURCE_INFO]: {
        id: record.srcID,
        type: record.srcType,
        name: ''
      },
      [AUDITLOG_COLUMN.EMAIL]: record.email,
      [AUDITLOG_COLUMN.EVENT]: record.funcType
    };
  };

  async initAuditLog(page = 1) {
    try {
      const pageable = {
        page: page,
        sizePerPage: 10,
        sort: this.sortField,
        direction: this.sortOrder
      };
      const auditLogFromServer =
        await this.auditLogManager.getChannelGroupAuditLog(
          this.channelGroupId,
          pageable
        );
      this.pagination = auditLogFromServer.pagination;
      this.nonExpandRowKey = [];
      this.auditLogData = auditLogFromServer.records.map(
        this.getAuditLogDataOfRecord
      );
      this.updateFilteredAuditLogData();
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.auditLogData = [];
    }
  }

  updateFilteredAuditLogData() {
    this.filteredAuditLogData = this.auditLogData ? this.auditLogData : [];
    return this.filteredAuditLogData;
  }

  getColumnFormatter(columnName) {
    switch (columnName) {
      case AUDITLOG_COLUMN.SOURCE_INFO:
        return formatters.srcInfoFormatter;
      case AUDITLOG_COLUMN.EVENT:
        return formatters.i18nFormatter;
      default:
        return;
    }
  }

  getColumns(): ColumnDescription<ChannelGroupAuditLogListRow>[] {
    const definitions = Object.values(AUDITLOG_COLUMN).map((columnToRender) => {
      // 明細走展開列，不是獨立欄位。
      if (columnToRender === AUDITLOG_COLUMN.DETAIL) {
        return undefined;
      }
      return renderColumn(
        this.getAuditLogColumnDefinition(columnToRender),
        this.getColumnFormatter(columnToRender)
      );
    });
    return _.compact(definitions);
  }

  getExpandRenderer(): ExpandRowProps<ChannelGroupAuditLogListRow, number> {
    return {
      nonExpandable: this.nonExpandRowKey,
      renderer: formatters.detailExpandRenderer
    };
  }
}
